
<template>
  <VisionCard
    :content="props.item.content"
    :link="isLinkOrOpenUI(props.item.link).isLink ? isLinkOrOpenUI(props.item.link).internalLink : null"
    :subtitle="props.item.subtitle"
    :subtitle-style="props.item.subtitleStyle"
    :aspect-ratio="props.aspectRatio"
    :bg-colour="props.item.bgColour.hex"
    :colour="props.item.colour"
    :scrim="props.item.scrim"
    :shadow="props.item.shadow"
    :alignment="props.item.alignment"
    :justification="props.item.justification"
    :icon-button="props.item.iconButton"
    :theme="props.item.linkedTheme"
    :has-button="props.item.content.some((block: any) => block.type === 'button_group')"
    :is-button="props.isButton || isLinkOrOpenUI(props.item.link).opensUI"
    :has-promo-theme="hasPromoTheme"
    :class="bem({hasPromoTheme})"
    @clicked="handleLinkClick(props.item.link)"
  >
    <template
      v-if="props.item.content"
      #content
    >
      <div
        v-if="$slots.additional"
        :class="bem('additional')"
      >
        <slot name="additional" />
      </div>
      <template v-for="(block, index) in props.item.content">
        <VisionHeading
          v-if="block.type === 'title_block'"
          :key="block.id"
          :tag="block.tag"
          class="VisionCard__title"
        >
          {{ block.text }}
        </VisionHeading>
        <p
          v-if="block.type === 'description'"
          :key="block.id"
          class="VisionCard__description"
        >
          {{ block.description }}
        </p>
        <div
          v-if="block.type === 'button_group'"
          :key="block.id"
          :class="bem('button-group', {
            'first': index === 0,
            'last': index === props.item.content.length - 1
          })"
        >
          <template v-for="button in block.buttons">
            <VisionButton
              v-if="buttonOrLineLink(button.style) === 'button'"
              :key="button.id"
              :to="isLinkOrOpenUI(button.link).isLink ? isLinkOrOpenUI(button.link).internalLink : null"
              :outline="button.style === 'outline'"
              size="x-small"
              :background-colour="buttonColours(button.style).backgroundColour"
              :text-colour="buttonColours(button.style).textColour"
              :button-text="button.text"
              :element="isLinkOrOpenUI(props.item.link).isLink && 'div'"
              :promo-button="hasPromoTheme"
              @click="handleLinkClick(button.link)"
            />
            <VisionLink
              v-if="buttonOrLineLink(button.style) === 'line'"
              :key="button.id"
              :theme="button.style === 'line-gradient' ? 'gradient' : props.item.colour"
              :to="isLinkOrOpenUI(button.link).isLink ? isLinkOrOpenUI(button.link).internalLink : null"
              line-link
              :has-promo-theme="hasPromoTheme"
              :element="isLinkOrOpenUI(props.item.link).isLink && 'div'"
              @click="handleLinkClick(button.link)"
            >
              {{ button.text }}
            </VisionLink>
          </template>
        </div>
        <VisionIcon
          v-if="block.type === 'icon'"
          :key="block.id"
          :colour="props.item.colour"
          :icon="block.icon"
          size="stretch"
          :class="bem('content-icon', {
            'first': index === 0,
            'last': index === props.item.content.length - 1
          })"
        />
        
        <SingleImage
          v-if="block.type === 'single_image'"
          :key="block.id"
          :image="block.image"
          :mobile-image="block.mobileImage"
          :max-width="`${block.maxWidth}${block.widthMode === 'percentage' ? '%' : 'px'}`"
          :min-width="`${block.minWidth}px`"
          :class="bem('single-image')"
        />

        <HighlightHeading
          v-if="block.type === 'highlighter_heading'"
          :key="block.id"
          :heading="block"
          :size="block.size"
        />

      </template>
    </template>
    <template
      v-if="props.item.image"
      #image
    >
      <DatoCmsImage
        :data="props.item.image.responsiveImage"
        object-fit="cover"
        :object-position="`${props.item.image.focalPoint.x * 100}% ${
          props.item.image.focalPoint.y * 100
        }%`"
        layout="fill"
        :class="bem('image', {'hide-on-mobile': !!props.item.mobileImage})"
        sizes="(max-width: 1280px) 100vw, 40vw"
      />
      <DatoCmsImage
        v-if="props.item.mobileImage"
        :data="props.item.mobileImage.responsiveImage"
        object-fit="cover"
        :object-position="`${props.item.mobileImage.focalPoint.x * 100}% ${
          props.item.mobileImage.focalPoint.y * 100
        }%`"
        layout="fill"
        :class="bem('image', {'mobile-image': true})"
        sizes="(max-width: 1280px) 100vw, 40vw"
      />
    </template>
  </VisionCard>
</template>

<script setup lang="ts">
import { Image as DatoCmsImage } from 'vue-datocms'

const bem = useBEM('UniversalCard')
const promoStore = usePromoStore()

const { isLinkOrOpenUI, openUIElement } = useUrlBuilder()

const emits = defineEmits(['clicked'])

const props = defineProps({
  item: { type: Object, required: true },
  aspectRatio: {type: String, required: false, default: '4/5'},
  isButton: {type: Boolean, default: false}
})

const hasPromoTheme = computed(() => {
  return (
    props.item.linkedTheme?.type === 'special' &&
    props.item.linkedTheme?.id === promoStore.theme.id
  )
})

function buttonOrLineLink(type: string) {
  if (type === 'line' || type === 'line-gradient') return 'line'
  return 'button'
}

function buttonColours(style: string) {
  if (style === 'gradient') {
    return {
      backgroundColour: 'gradient',
      textColour: 'white'
    }
  }
  if (props.item.colour === 'black') {
    return {
      backgroundColour: 'black',
      textColour: 'white'
    }
  }
  return {
    backgroundColour: 'white',
    textColour: 'black'
  }
}

function handleLinkClick(link: any) {
  if(isLinkOrOpenUI(link).opensUI) {
    openUIElement(link)
  }
  emits('clicked')
}
</script>

<style lang="postcss">
.UniversalCard__image {
  @apply min-h-full object-cover;

  &--mobile-image {
    @apply block;
  }

  &--hide-on-mobile {
    @apply hidden
  }
}

@container card (min-width: 401px) {
  .UniversalCard__image--mobile-image {
    @apply hidden;
  }

  .UniversalCard__image--hide-on-mobile {
    @apply block;
  }
}

.UniversalCard__button-group {
  @apply flex gap-2 flex-wrap items-center
}

.UniversalCard__button-group,
.UniversalCard__content-icon {
  @apply my-2.5;

  &--first {
    @apply mt-0;
  }

  &--last {
    @apply mb-0;
  }
}

.UniversalCard__single-image {
  @apply mb-0 pb-0;
}

.UniversalCard__content-icon {
  @apply my-1.5
}

.UniversalCard__additional {
  @apply mb-2;
}

.UniversalCard__content-icon {
  width: var(--icon-width);
}
</style>
